import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageSEO from '@/components/base/PageSEO';
import MovieCard from '@/components/base/MovieCard';
import ScrollReveal from '@/components/base/ScrollReveal';
import {
  movieCategories,
  getHotMovies,
  getNewMovies,
  allMovies,
  siteInfo,
} from '@/mocks/movies';

export default function HomePage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [visibleCount, setVisibleCount] = useState(8);
  const sectionRef = useRef<HTMLDivElement>(null);

  const hotMovies = getHotMovies();
  const newMovies = getNewMovies();
  const filteredMovies =
    activeCategory === 'all'
      ? allMovies
      : allMovies.filter((m) => m.category === activeCategory);

  const displayedMovies = filteredMovies.slice(0, visibleCount);
  const hasMore = visibleCount < filteredMovies.length;

  const loadMore = () => {
    setVisibleCount((prev) => prev + 8);
  };

  useEffect(() => {
    setVisibleCount(8);
  }, [activeCategory]);

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: siteInfo.fullName,
    description: siteInfo.description,
    url: 'https://555mov.com',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: allMovies.slice(0, 10).map((movie, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Movie',
          name: movie.title,
          url: `https://555mov.com/article/${movie.slug}`,
          image: movie.poster,
          dateCreated: movie.releaseDate,
          director: {
            '@type': 'Person',
            name: movie.director,
          },
          actor: movie.actors.map((a) => ({
            '@type': 'Person',
            name: a,
          })),
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: movie.rating,
            bestRating: '10',
          },
        },
      })),
    },
  };

  const scrollToSection = () => {
    sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <PageSEO
        title={siteInfo.fullName + ' | ' + siteInfo.slogan}
        description={siteInfo.description}
        keywords={siteInfo.keywords}
        canonicalUrl="https://555mov.com"
        schema={homeSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full h-[500px] md:h-[650px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20movie%20theater%20interior%20with%20glowing%20red%20velvet%20curtains%20and%20golden%20spotlights%2C%20atmospheric%20fog%2C%20dramatic%20lighting%2C%20empty%20luxury%20cinema%20hall%2C%20professional%20photography%2C%20rich%20contrast%2C%20elegant%20composition&width=1800&height=900&seq=hero-bg-555&orientation=landscape"
            alt="555影视 - 最新电影高清电影大全"
            className="w-full h-full object-cover object-top"
            title="555影视 - 最新电影高清电影大全"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-wide">
              最新电影<span className="text-primary-500">高清</span>大片
            </h1>
            <p className="text-base md:text-xl text-gray-200 mb-8 max-w-2xl">
              免费在线观看，画质清晰播放流畅不缓冲，满足您的追影需求
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <button
                onClick={scrollToSection}
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer text-sm"
              >
                立即观影
              </button>
              <Link
                to="/about"
                className="px-8 py-3 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-semibold rounded-lg transition-all whitespace-nowrap text-sm border border-white/20"
              >
                了解更多
              </Link>
            </div>
          </div>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
            <button
              onClick={scrollToSection}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all cursor-pointer animate-bounce"
              aria-label="向下滚动"
            >
              <i className="ri-arrow-down-line text-white text-lg"></i>
            </button>
          </div>
        </section>

        {/* Category Quick Nav */}
        <section className="bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide flex-wrap">
              <button
                onClick={() => setActiveCategory('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === 'all'
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                全部
              </button>
              {movieCategories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        <div ref={sectionRef}>
          {/* Hot Movies */}
          {activeCategory === 'all' && (
            <ScrollReveal>
              <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                    <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
                      热门推荐
                    </h2>
                  </div>
                  <Link
                    to="/category/dongzuo"
                    className="text-sm text-foreground-500 hover:text-primary-500 transition-colors flex items-center gap-1 whitespace-nowrap"
                  >
                    查看更多 <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                  {hotMovies.map((movie) => (
                    <MovieCard key={movie.id} {...movie} />
                  ))}
                </div>
              </section>
            </ScrollReveal>
          )}

          {/* New Movies */}
          {activeCategory === 'all' && (
            <ScrollReveal>
              <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
                    <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
                      最新上映
                    </h2>
                  </div>
                  <Link
                    to="/category/kehuan"
                    className="text-sm text-foreground-500 hover:text-primary-500 transition-colors flex items-center gap-1 whitespace-nowrap"
                  >
                    查看更多 <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                  {newMovies.map((movie) => (
                    <MovieCard key={movie.id} {...movie} />
                  ))}
                </div>
              </section>
            </ScrollReveal>
          )}

          {/* All Movies Grid */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
                {activeCategory === 'all' ? '全部电影' : movieCategories.find((c) => c.slug === activeCategory)?.name || '电影列表'}
              </h2>
            </div>

            {displayedMovies.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                  {displayedMovies.map((movie) => (
                    <MovieCard key={movie.id} {...movie} />
                  ))}
                </div>
                {hasMore && (
                  <div className="flex justify-center mt-8">
                    <button
                      onClick={loadMore}
                      className="px-8 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-lg transition-all whitespace-nowrap cursor-pointer text-sm"
                    >
                      加载更多
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16 text-foreground-500">
                <i className="ri-movie-line text-4xl block mb-3"></i>
                <p>暂时没有该分类的电影</p>
              </div>
            )}
          </section>
        </div>

        {/* Features Section */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <ScrollReveal>
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  为什么选择555影视
                </h2>
                <p className="text-foreground-600 max-w-2xl mx-auto text-sm">
                  我们致力于为您提供最佳的在线观影体验
                </p>
              </div>
            </ScrollReveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-movie-2-line', title: '海量资源', desc: '涵盖各类电影资源，持续更新' },
                { icon: 'ri-4k-line', title: '高清画质', desc: '高清片源，视觉享受不打折' },
                { icon: 'ri-speed-line', title: '流畅播放', desc: '极速加载不缓冲，观影更顺畅' },
                { icon: 'ri-shield-check-line', title: '免费观影', desc: '无需付费，免费畅享大片' },
              ].map((feature, idx) => (
                <ScrollReveal key={idx} delay={idx * 100}>
                  <div
                    className="bg-white rounded-xl p-6 text-center hover:-translate-y-1 transition-transform duration-300"
                  >
                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-50 flex items-center justify-center">
                      <i className={`${feature.icon} text-2xl text-primary-500`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-950 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-foreground-500">{feature.desc}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}